import { api, unwrap } from '../../shared/api/client';
import type { components } from '../../shared/api/schema.gen';

export type TOrganization = components['schemas']['OrganizationResDto'];
export type TOrganizationSaved = components['schemas']['UpdateOrganizationResDto'];
export type TOrganizationUpdate = components['schemas']['UpdateOrganizationReqDto'];
export type TSlugChange = components['schemas']['SlugChangeResDto'];
export type TMember = components['schemas']['AccessGrantResDto'];
export type TRole = TMember['role'];
export type TInvitation = components['schemas']['InvitationResDto'];
export type TInvitationCreated = components['schemas']['InvitationCreatedResDto'];
export type TResetLink = components['schemas']['MemberResetLinkResDto'];

export type TAiBudgetToday = components['schemas']['AiBudgetTodayResDto'];

export const getOrganization = (): Promise<TOrganization> =>
  unwrap(api.GET('/v1/organizations/current'));

/** Today's AI spend against the limit in force; the limit itself is set on the organization. */
export const getAiBudgetToday = (): Promise<TAiBudgetToday> =>
  unwrap(api.GET('/v1/flows/ai-budget'));

export type TAiUsage = components['schemas']['AiUsageResDto'];
export type TAiUsageWindow = TAiUsage['window'];

export const getAiUsage = (window: TAiUsageWindow): Promise<TAiUsage> =>
  unwrap(api.GET('/v1/flows/ai-usage', { params: { query: { window } } }));

export const createOrganization = (name: string): Promise<TOrganization> =>
  unwrap(api.POST('/v1/organizations', { body: { name } }));

export const updateOrganization = (body: TOrganizationUpdate): Promise<TOrganizationSaved> =>
  unwrap(api.PATCH('/v1/organizations/current', { body }));

export const deleteOrganization = async (confirmName: string): Promise<void> => {
  await unwrap(api.DELETE('/v1/organizations/current', { body: { confirmName } }));
};

export const listMembers = (): Promise<TMember[]> => unwrap(api.GET('/v1/access-grants'));

export const setMemberRole = (userId: string, role: TRole): Promise<TMember> =>
  unwrap(api.PATCH('/v1/access-grants/{userId}', { params: { path: { userId } }, body: { role } }));

export const removeMember = async (userId: string): Promise<void> => {
  await unwrap(api.DELETE('/v1/access-grants/{userId}', { params: { path: { userId } } }));
};

export const leaveOrganization = async (): Promise<void> => {
  await unwrap(api.POST('/v1/access-grants/leave'));
};

export const listInvitations = (): Promise<TInvitation[]> => unwrap(api.GET('/v1/invitations'));

export const inviteMember = (email: string, role: TRole): Promise<TInvitationCreated> =>
  unwrap(api.POST('/v1/invitations', { body: { email, role } }));

export const revokeInvitation = async (id: string): Promise<void> => {
  await unwrap(api.DELETE('/v1/invitations/{id}', { params: { path: { id } } }));
};

export const memberResetLink = (userId: string): Promise<TResetLink> =>
  unwrap(api.POST('/v1/auth/members/{userId}/reset-link', { params: { path: { userId } } }));
